<script setup>
import { ref, computed, reactive } from 'vue'
import { Play, FileImage, Video, AudioLines } from 'lucide-vue-next'
import MediaSliderDialog from './MediaSliderDialog.vue'
import { deleteResource } from '@/services/resourcesApi'
import { useToast } from '@/components/ui/toast/use-toast'

const props = defineProps({
  resources: {
    type: Array,
    default: () => []
  },
  showDelete: {
    type: Boolean,
    default: true
  },
  deleteHandler: {
    type: Function,
    default: null
  }
})

const { toast } = useToast()

const sliderOpen = ref(false)
const selectedIndex = ref(0)
const deletedUuids = reactive(new Set())
const isDeleting = ref(false)

const processedResources = computed(() => {
  if (!props.resources || props.resources.length === 0) return []

  const grouped = {}

  props.resources.forEach(resource => {
    const uuid = resource.uuid || resource.id
    if (!uuid) return

    if (!grouped[uuid]) {
      grouped[uuid] = {
        uuid: uuid,
        file_type: resource.file_type || 'image',
        filename: resource.filename,
        variants: {}
      }
    }

    if (resource.type === 'small' || resource.type === 'thumbnail' || resource.type === 'fullsize') {
      grouped[uuid].variants[resource.type] = resource
      grouped[uuid].file_type = resource.file_type || 'image'
    } else if (resource.type === 'original') {
      grouped[uuid].variants['original'] = resource
      grouped[uuid].file_type = resource.file_type
    } else {
      grouped[uuid].variants[resource.type || 'fullsize'] = resource
    }

    if (resource.filename) {
      grouped[uuid].filename = resource.filename
    }
  })

  return Object.values(grouped).map(group => {
    const variant = group.variants.thumbnail || group.variants.small || group.variants.fullsize || group.variants.original || Object.values(group.variants)[0]

    return {
      ...variant,
      uuid: group.uuid,
      type: group.file_type,
      file_type: group.file_type,
      filename: group.filename || variant.filename,
      variant_urls: {
        small: group.variants.small?.url,
        thumbnail: group.variants.thumbnail?.url,
        fullsize: group.variants.fullsize?.url,
        original: group.variants.original?.url
      },
      all_variants: group.variants
    }
  }).filter(item => !deletedUuids.has(item.uuid))
})

const hasMedia = computed(() => processedResources.value.length > 0)

const handlePreviewClick = (index) => {
  selectedIndex.value = index
  sliderOpen.value = true
}

const handleResourceDelete = async (resource) => {
  if (isDeleting.value) return

  const uuid = resource?.uuid || resource?.id
  if (!uuid) {
    toast({ title: 'Error', description: 'Unable to identify resource', variant: 'destructive' })
    return
  }

  const isLastItem = processedResources.value.length <= 1

  // Optimistic update — immediately remove from grid
  deletedUuids.add(uuid)

  isDeleting.value = true
  try {
    await deleteResource(uuid)
    if (isLastItem) sliderOpen.value = false
    await props.deleteHandler?.(resource)
  } catch (error) {
    // Rollback on failure
    deletedUuids.delete(uuid)
    toast({ title: 'Error', description: error.message || 'Failed to delete resource', variant: 'destructive' })
  } finally {
    isDeleting.value = false
  }
}
</script>

<template>
  <div v-if="hasMedia" class="media-list">
    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
      <div
        v-for="(resource, index) in processedResources"
        :key="resource.id || resource.uuid || index"
        class="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer hover:opacity-90 transition-opacity"
      >
        <div
          @click="handlePreviewClick(index)"
          class="absolute inset-0 z-10"
        >
          <template v-if="resource.type === 'image'">
            <img
              :src="resource.variant_urls?.thumbnail || resource.thumbnail_url || resource.url"
              :alt="resource.filename || 'Image'"
              class="w-full h-full object-cover"
              loading="lazy"
            />
          </template>

          <template v-else-if="resource.type === 'video'">
            <div class="w-full h-full bg-gray-200 flex items-center justify-center">
              <Video class="w-8 h-8 text-gray-500" />
            </div>
            <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div class="bg-black/50 rounded-full p-2">
                <Play class="w-3 h-3 text-white fill-white" />
              </div>
            </div>
            <div class="absolute bottom-1 left-1 bg-black/50 rounded-md p-0.5 pointer-events-none">
              <Video class="w-3.5 h-3.5 text-white" />
            </div>
          </template>

          <template v-else-if="resource.type === 'audio'">
            <div class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600">
              <div class="bg-white/20 rounded-full p-2">
                <Play class="w-3 h-3 text-white fill-white" />
              </div>
              <div class="absolute bottom-1 left-1 bg-white/20 rounded-md p-0.5">
                <AudioLines class="w-3.5 h-3.5 text-white" />
              </div>
            </div>
          </template>

          <template v-else>
            <div class="w-full h-full flex items-center justify-center bg-gray-200">
              <FileImage class="w-12 h-12 text-gray-500" />
            </div>
          </template>
        </div>
      </div>
    </div>

    <MediaSliderDialog
      v-model:open="sliderOpen"
      :resources="processedResources"
      :initial-index="selectedIndex"
      :show-delete="showDelete"
      @delete="handleResourceDelete"
    />
  </div>
</template>
